import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { LOGIN } from '../constants/routes';
import Header from '../components/Header';
import DashboardSidebar from '../components/dashboard/DashboardSidebar';
import { fetchDashboardData } from './../services/dashboardServices';
import localStorageService, { SELECTED_SPORT_CHANGE_EVENT } from '../services/localStorageService';
import { DashboardApiResponse, DashboardOutletContextValue } from '../types/dashboard';
import { buildDashboardModules } from '../utils/dashboardModules';

type StoredUser = {
  username?: string;
  userName?: string;
  name?: string;
};

function Dashboard() {
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardApiResponse | null>(null);
  const [selectedSport, setSelectedSport] = useState(
    localStorageService.getSelectedSport() || 'MFB'
  );
  const userData = localStorageService.getUserInfo<StoredUser>();
  const username = userData?.username || userData?.userName || userData?.name || '';
  const modules = useMemo(() => buildDashboardModules(dashboardData), [dashboardData]);

  useEffect(() => {
    const syncSelectedSport = () => {
      setSelectedSport(localStorageService.getSelectedSport() || 'MFB');
    };

    window.addEventListener(SELECTED_SPORT_CHANGE_EVENT, syncSelectedSport);
    window.addEventListener('storage', syncSelectedSport);

    return () => {
      window.removeEventListener(SELECTED_SPORT_CHANGE_EVENT, syncSelectedSport);
      window.removeEventListener('storage', syncSelectedSport);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!username) {
        setDashboardError('Username is missing. Please login again.');
        return;
      }

      setIsLoading(true);
      setDashboardError(null);

      try {
        const data = await fetchDashboardData({
          username,
          selectedSport,
        });
        setDashboardData(data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setDashboardError('Unable to fetch dashboard data.');
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [selectedSport, username]);

  if (!userData) {
    return <Navigate replace to={LOGIN} />;
  }

  const outletContext: DashboardOutletContextValue = {
    username,
    selectedSport,
    dashboardData,
    isLoading,
    error: dashboardError,
    modules,
  };

  return (
    <main className="flex w-full flex-1 flex-col bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.14),_transparent_30%),linear-gradient(180deg,_#f9fbff_0%,_#eef4ff_48%,_#f8fbff_100%)] text-slate-900">
      <Header />
      <div className="flex flex-1 flex-col md:flex-row">
        <DashboardSidebar modules={modules} />
        <div className="flex-1 px-4 py-6 md:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1500px]">
            <Outlet context={outletContext} />
          </div>
        </div>
      </div>
    </main>
  );
}

export default Dashboard;
